import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroArrowLeft } from '@ng-icons/heroicons/outline';
import { UiIconButton } from '../../../shared/ui';
import { ConversationFiltersStore, type ConversationRoutePreset } from '../data/conversation-filters.store';
import { ConversationsStore } from '../data/conversations.store';
import { ConversationListComponent } from '../components/conversation-list/conversation-list';
import { DetailsPanelComponent } from '../components/details-panel/details-panel';
import { FilterRailComponent } from '../components/filter-rail/filter-rail';
import { ThreadViewComponent } from '../components/thread/thread-view';

/**
 * Conversas workspace shell. Hosts the four panels, owns the inbox→thread layout, and keeps the
 * selected conversation in sync with the `:conversationId` route. Data/filter logic lives in the
 * injected stores; this component only orchestrates.
 */
@Component({
  selector: 'app-conversations-page',
  imports: [NgIcon, UiIconButton, FilterRailComponent, ConversationListComponent, ThreadViewComponent, DetailsPanelComponent],
  providers: [ConversationFiltersStore],
  viewProviders: [provideIcons({ heroArrowLeft })],
  templateUrl: './conversations.page.html',
  styleUrl: './conversations.page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationsPage {
  protected readonly store = inject(ConversationsStore);
  protected readonly filters = inject(ConversationFiltersStore);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  private lastNavigatedRouteKey = '';
  private readonly activeViewPath = signal('visao-geral');
  protected readonly mobileListVisible = signal(true);

  constructor() {
    this.store.connectRealtime();

    // The route preset drives the (single) initial load: applyRoutePreset always reloads with
    // explicit server filters and preserveSelection, so a bare loadConversations() here would
    // only duplicate the request and reset the selection to the first conversation.
    this.route.data.pipe(takeUntilDestroyed()).subscribe((data) => {
      this.activeViewPath.set(readConversationViewPath(data['conversationsViewPath']));
      this.filters.applyRoutePreset(readConversationPreset(data['conversationsPreset']));
    });

    // Route param → selection.
    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      const conversationId = params.get('conversationId');
      if (conversationId) {
        this.lastNavigatedRouteKey = routeKey(this.activeViewPath(), conversationId);
      } else {
        this.lastNavigatedRouteKey = '';
      }
      if (conversationId && conversationId !== this.store.selectedConversationId()) {
        this.store.selectConversation(conversationId);
      }
    });

    // Selection → route URL (deep-link, back/forward).
    effect(() => {
      const selectedId = this.store.selectedConversationId();
      const activeViewPath = this.activeViewPath();
      const nextRouteKey = routeKey(activeViewPath, selectedId);

      if (selectedId && nextRouteKey !== this.lastNavigatedRouteKey) {
        this.lastNavigatedRouteKey = nextRouteKey;
        void this.router.navigate(['/conversas', activeViewPath, selectedId]);
      }
    });

    // Keep a visible conversation selected when filters change the list.
    effect(() => {
      const visible = this.filters.conversations();
      const selectedId = this.store.selectedConversationId();
      if (visible.length === 0) {
        return;
      }
      if (!visible.some((conversation) => conversation.id === selectedId)) {
        this.store.selectConversation(visible[0].id);
      }
    });
  }

  openConversation(): void {
    this.mobileListVisible.set(false);
  }

  showConversationList(): void {
    this.mobileListVisible.set(true);
  }
}

function readConversationPreset(value: unknown): ConversationRoutePreset {
  return value === 'mine' || value === 'waiting' ? value : 'overview';
}

function readConversationViewPath(value: unknown): string {
  return typeof value === 'string' && value.length > 0 ? value : 'visao-geral';
}

function routeKey(viewPath: string, conversationId: string | null): string {
  return conversationId ? `${viewPath}/${conversationId}` : '';
}
